import type { ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

/**
 * Centered glass modal used across the new widgets (Slack question flow, request
 * access, send-to-ARES, confirmations). Renders within the window content so it
 * stays scoped to the app it was opened from.
 */
export function Modal({
  open,
  onClose,
  children,
  title,
  subtitle,
  icon,
  accent = "#f59e0b",
  width = 480,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  accent?: string;
  width?: number;
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="absolute inset-0 z-[60] flex items-center justify-center p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 360, damping: 30 }}
            className="relative z-10 flex max-h-full w-full flex-col overflow-hidden rounded-2xl border border-app-strong glass-strong shadow-window"
            style={{ maxWidth: width }}
          >
            <div className="flex items-start gap-3 border-b border-app px-5 py-4">
              {icon && (
                <div
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-white shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${accent}, ${accent}99)` }}
                >
                  {icon}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-app">{title}</div>
                {subtitle && <div className="mt-0.5 text-xs text-app-2">{subtitle}</div>}
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-1 text-app-2 transition hover:bg-surface-2"
              >
                <X size={16} />
              </button>
            </div>
            <div className="overflow-y-auto no-scrollbar p-5">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
